import z from 'zod';

const emailSchema = z.object({
  value: z.string().email('Invalid Email').or(z.literal('')),
  label: z.string(),
});

const addressSchema = z.object({
  country: z.string(),
  street: z.string(),
  street2: z.string(),
  city: z.string(),
  state: z.string(),
  zip: z.string(),
  pobox: z.string(),
  label: z.string(),
});

const contactFormSchema = z
  .object({
    avatar: z.string().nullable().optional(),
    labelIds: z.array(z.object({ value: z.number() })),
    firstName: z.string(),
    lastName: z.string(),
    prefix: z.string().optional(),
    middleName: z.string().optional(),
    suffix: z.string().optional(),
    phoneticFirst: z.string().optional(),
    phoneticMiddle: z.string().optional(),
    phoneticLast: z.string().optional(),
    nickname: z.string().optional(),
    fileAs: z.string().optional(),
    emails: z.array(emailSchema),
    phoneNumbers: z.array(z.object({ value: z.string(), label: z.string() })),
    address: z.array(addressSchema).nullable(),
    companyName: z.string().nullable(),
    companyJobTitle: z.string().nullable(),
    companyDepartment: z.string().nullable(),
    birthday: z
      .object({
        month: z.string(),
        day: z.string(),
        year: z.string(),
      })
      .nullable(),
    significantDates: z
      .array(
        z.object({
          month: z.string(),
          day: z.string(),
          year: z.string(),
          label: z.string(),
        })
      )
      .nullable(),
    websites: z
      .array(z.object({ value: z.string(), label: z.string() }))
      .nullable(),
    relatedPersons: z
      .array(z.object({ value: z.string(), label: z.string() }))
      .nullable(),
    customFields: z
      .array(z.object({ value: z.string(), label: z.string() }))
      .nullable(),
    notes: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    const hasEmail = data.emails?.some((e) => e.value?.trim());
    const hasPhone = data.phoneNumbers?.some((p) => p.value?.trim());
    const hasAddress = data.address?.some(
      (a) =>
        a.country?.trim() &&
        (a.street?.trim() ||
          a.street2?.trim() ||
          a.city?.trim() ||
          a.state?.trim() ||
          a.zip?.trim() ||
          a.pobox?.trim())
    );

    if (!hasEmail && !hasPhone && !hasAddress) {
      const message = 'Email, Phone or Address is required.';
      ctx.addIssue({
        code: 'custom',
        message,
        path: ['emails', 0, 'value'],
      });
      ctx.addIssue({
        code: 'custom',
        message,
        path: ['phoneNumbers', 0, 'value'],
      });

      addressErrorFields(ctx, 0);
    }

    if (!data.firstName?.trim() && !data.lastName?.trim()) {
      ctx.addIssue({
        code: 'custom',
        message: 'First name or last name is required.',
        path: ['firstName'],
      });
      ctx.addIssue({
        code: 'custom',
        message: 'First name or last name is required.',
        path: ['lastName'],
      });
    }

    data.address?.forEach((address, index) => {
      const hasOtherField =
        address.street?.trim() ||
        address.street2?.trim() ||
        address.city?.trim() ||
        address.state?.trim() ||
        address.zip?.trim() ||
        address.pobox?.trim();

      //city is auto populated and will never be null or empty
      if (!hasOtherField) {
        addressErrorFields(ctx, index);
      }
    });
  });

const addressErrorFields = (ctx: z.RefinementCtx, index: number) => {
  ctx.addIssue({
    code: 'custom',
    message: 'At least one other address field is required.',
    path: ['address', index, 'street'],
  });
  ctx.addIssue({
    code: 'custom',
    message: 'At least one other address field is required.',
    path: ['address', index, 'street2'],
  });
  ctx.addIssue({
    code: 'custom',
    message: 'At least one other address field is required.',
    path: ['address', index, 'city'],
  });
  ctx.addIssue({
    code: 'custom',
    message: 'At least one other address field is required.',
    path: ['address', index, 'state'],
  });
  ctx.addIssue({
    code: 'custom',
    message: 'At least one other address field is required.',
    path: ['address', index, 'zip'],
  });
  ctx.addIssue({
    code: 'custom',
    message: 'At least one other address field is required.',
    path: ['address', index, 'pobox'],
  });
};

export { emailSchema, addressSchema, contactFormSchema };
